import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error, but don't let browser extension errors break the app
    console.error('Error boundary caught an error:', error, errorInfo);
    
    // Filter out browser extension errors
    const isBrowserExtensionError = 
      error.message?.includes('message channel closed') ||
      error.message?.includes('Extension context invalidated') ||
      error.stack?.includes('extension://');
    
    if (!isBrowserExtensionError) {
      this.setState({
        error: error,
        errorInfo: errorInfo
      });
    } else {
      // For browser extension errors, just log and reset
      console.warn('Browser extension error detected, ignoring:', error.message);
      setTimeout(() => {
        this.setState({ hasError: false, error: null, errorInfo: null });
      }, 100);
    }
  }

  render() {
    if (this.state.hasError && this.state.error) {
      // Don't show error UI for browser extension errors
      const isBrowserExtensionError = 
        this.state.error.message?.includes('message channel closed') ||
        this.state.error.message?.includes('Extension context invalidated');
      
      if (isBrowserExtensionError) {
        return this.props.children;
      }

      // Show error UI for actual app errors
      return (
        <div className="min-h-screen flex items-center justify-center bg-brand-dark">
          <div className="text-center max-w-md">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <h2 className="font-bold text-lg mb-2">Something went wrong</h2>
              <p className="text-sm">An unexpected error occurred. Please refresh the page.</p>
              {process.env.NODE_ENV === 'development' && (
                <details className="mt-2 text-xs">
                  <summary>Error details</summary>
                  <pre className="mt-1 whitespace-pre-wrap">
                    {this.state.error && this.state.error.toString()}
                    <br />
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
            <button
              onClick={() => window.location.reload()}
              className="bg-brand hover:bg-brand-hover text-brand-dark font-bold py-2 px-4 rounded"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
